import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
// import { useSelector } from "react-redux";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import { LIKE } from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import NumberInput from "../../framework/NumberInput";
import SelectInput from "../../framework/SelectInput";
import Shortcut from "../../framework/Shortcut";
import TextInput from "../../framework/TextInput";
import { getCustomerCategories } from "../../services/customer-category-services";
import { listEntity } from "../../services/entity-services";
import { isEmpty } from "../../utils/utils";
import { debounceCategoryName, debounceEntityName } from "./entityUtils";

const EntityReportFilter = ({
  reportRef,
  onFilterShow,
  refreshData = false,
  // applyFilters = () => {},
  applyFilterOnMount = true,
}) => {
  const [entityNameOptions, setEntityNameOptions] = useState([]);
  const [entityName, setEntityName] = useState("");
  const [entityCategoryOptions, setEntityCategoryOptions] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [contactNumber, setEntityContactNumber] = useState("");
  const [panNumber, setEntityPanNumber] = useState("");
  const [gstinNumber, setGstinNumber] = useState("");

  const [orderBy] = useState([{ fieldName: "entityName", orderType: "asc" }]);

  const validateLength = ({ value }) => value.length <= 10;
  const apr_filter_entity_name_ref = useRef();
  const apr_filter_entity_category_ref = useRef();
  const applyFilterRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};
      if (!isEmpty(entityName)) {
        filters.push(
          createFilterParam("entityName", `${entityName}`, "string", LIKE)
        );
      }
      if (!isEmpty(categoryName)) {
        filters.push(
          createFilterParam("categoryId", `${categoryName?.id}`, "string", LIKE)
        );
      }

      if (!isEmpty(contactNumber)) {
        filters.push(
          createFilterParam("contactNumber", `${contactNumber}`, "string", LIKE)
        );
      }

      if (!isEmpty(panNumber)) {
        filters.push(
          createFilterParam("panNumber", `${panNumber}`, "string", LIKE)
        );
      }

      if (!isEmpty(gstinNumber)) {
        filters.push(
          createFilterParam("gstin", `${gstinNumber}`, "string", LIKE)
        );
      }

      if (orderBy?.length) {
        result.orderBy = orderBy;
      }
      result.filters = filters;
      result.isFilterChanged = isFilterChanged;

      reportRef.current?.applyFilters(result);
    },
    [
      entityName,
      categoryName,
      contactNumber,
      panNumber,
      gstinNumber,
      orderBy,
      reportRef,
    ]
  );

  const filterListener = useCallback((event) => {
    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey;
    if (ctrl && key === "enter") {
      return applyFilterRef.current.click();
    }
  }, []);

  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="apr_filter_customer_name"
            label="Entity Name"
            ref={apr_filter_entity_name_ref}
            options={entityNameOptions}
            getOptionLabel={(option) => option.entityName || option}
            isOptionEqualToValue={(option, value) => {
              return option.entityId === value.entityId;
            }}
            onFocus={() => setCurrentComponent("apr_filter_customer_name")}
            freeSolo
            onChange={(e, values) => {
              setEntityName(values.entityName);
            }}
            value={entityName}
            onInputChange={(e) => {
              if (!e) return;
              setEntityName(e.target.value);
              setEntityNameOptions([]);
              if (!e?.target?.value) setEntityName("");
              if (e.target.value?.length > 0)
                debounceEntityName(
                  listEntity,
                  e.target.value,
                  setEntityNameOptions
                );
              // debouncedListEntity(e.target.value, setEntityNameOptions);
            }}
            markers={JSON.stringify({
              up: "apr_filter_customer_name",
              down: "apr_filter_customer_category",
              right: "apr_filter_customer_category",
              left: "apr_filter_customer_name",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="apr_filter_customer_category"
            label="Entity Category"
            ref={apr_filter_entity_category_ref}
            options={entityCategoryOptions}
            getOptionLabel={(option) => option.name || option}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            onFocus={() => setCurrentComponent("apr_filter_customer_category")}
            freeSolo
            onChange={(e, values) => {
              setCategoryName(values);
            }}
            value={categoryName?.name || ""}
            onInputChange={(e) => {
              if (!e) return;
              setCategoryName(e.target.value);
              setEntityCategoryOptions([]);
              if (!e?.target?.value) setCategoryName("");
              if (e.target.value.length > 0)
                debounceCategoryName(
                  getCustomerCategories,
                  e.target.value,
                  setEntityCategoryOptions
                );
            }}
            markers={JSON.stringify({
              up: "apr_filter_customer_name",
              down: "apr_filter_contact_number",
              right: "apr_filter_contact_number",
              left: "apr_filter_customer_name",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="apr_filter_contact_number"
            label="Contact Number"
            allowNegative={false}
            isAllowed={validateLength}
            onFocus={() => {
              setCurrentComponent("apr_filter_contact_number");
            }}
            onValueChange={(values, e) => {
              setEntityContactNumber(values.formattedValue);
            }}
            value={contactNumber}
            decimalScale={0}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_customer_category",
                down: "apr_filter_pan_number",
                right: "apr_filter_pan_number",
                left: "apr_filter_customer_category",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="apr_filter_pan_number"
            label="PAN Number"
            value={panNumber}
            onKeyDown={(e) => {
              if (e.key === " ") {
                e.preventDefault();
              }
              if (!/^[A-Za-z0-9]*$/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              if (e.target.value?.length > 10) return;
              setEntityPanNumber(e.target.value.toUpperCase());
            }}
            onFocus={() => {
              setCurrentComponent("apr_filter_pan_number");
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_contact_number",
                down: "apr_filter_gstin_number",
                right: "apr_filter_gstin_number",
                left: "apr_filter_contact_number",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="apr_filter_gstin_number"
            label="GSTIN"
            value={gstinNumber}
            onChange={(e) => {
              setGstinNumber(e.target.value);
            }}
            onFocus={() => {
              setCurrentComponent("apr_filter_gstin_number");
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_pan_number",
                down: "apr_apply_filter_button",
                right: "apr_apply_filter_button",
                left: "apr_filter_pan_number",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="apr_apply_filter_button"
            label="Apply"
            ref={applyFilterRef}
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => {
              applyFilterHandler(true);
            }}
            markers={JSON.stringify({
              up: "apr_filter_gstin_number",
              left: "apr_filter_gstin_number",
              right: "apr_apply_filter_button",
              down: "apr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  useEffect(() => {
    if (onFilterShow) {
      apr_filter_entity_name_ref?.current.focus({ preventScroll: true });
    }
  }, [onFilterShow]);
  useEffect(() => {
    if (onFilterShow) document.addEventListener("keyup", filterListener, false);
    return () => {
      document.removeEventListener("keyup", filterListener, false);
    };
  }, [filterListener, onFilterShow]);

  useEffect(() => {
    if (applyFilterOnMount) applyFilterHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFilterOnMount]);

  return <Filters filterContent={filterContent} filterFooter={filterFooter} />;
};

export default EntityReportFilter;
